import { NetworkStatus } from "@apollo/client";
import { Loading, useMobile } from "@cpj-ai/panel-npm-package";
import { GetJobPositionInterviewQuery, useGetJobPositionInterviewQuery } from "@graphql/generated";
import { Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import React from "react";
import CandidateInfoPanel from "./CandidateInfoPanel";
import InterviewTimeline from "./InterviewTimeline";
import JobPositionInfoCard from "./JobPositionInfoCard";

interface InterviewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  interviewId?: string;
}

export type TJobPositionAssignment = NonNullable<
  NonNullable<GetJobPositionInterviewQuery["organization"]>["jobPositionInterview"]
>["jobPositionAssignment"];

export type TJobPosition = NonNullable<
  NonNullable<GetJobPositionInterviewQuery["organization"]>["jobPositionInterview"]
>["jobPosition"];

export type TJobPositionInterviews = NonNullable<TJobPositionAssignment>["interviews"];

export type TJobSeeker = NonNullable<
  NonNullable<
    NonNullable<GetJobPositionInterviewQuery["organization"]>["jobPositionInterview"]
  >["jobPositionAssignment"]
>["jobSeeker"];

export type TJobPositionAssignmentItem = NonNullable<
  NonNullable<NonNullable<TJobPosition>["assignments"]>["edges"][number]
>["node"];

const InterviewDetailModal: React.FC<InterviewDetailModalProps> = ({
  isOpen,
  onClose,
  interviewId = "interview-1",
}) => {
  const isMobile = useMobile();
  const { data, loading, refetch, networkStatus } = useGetJobPositionInterviewQuery({
    variables: {
      id: interviewId,
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[1300px]"
      size={isMobile ? "full" : "xl"}
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => {
          if (loading || networkStatus === NetworkStatus.refetch) return <Loading className="py-48" />;

          if (!data) return <div className="py-48 text-center">No data</div>;

          const firstAssignment = data?.organization?.jobPositionInterview?.jobPosition?.assignments?.edges?.[0]?.node;

          return (
            <>
              <ModalHeader className="flex items-center gap-4 border-b pb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold">Interview Details</h2>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-gray-500 text-sm">
                    <span>ID: {interviewId}</span>
                    {/* <span>•</span>
                    <span>Status: {data?.organization?.jobPositionInterview?.jobPositionAssignment?.status}</span> */}
                  </div>
                </div>
              </ModalHeader>

              <ModalBody className="p-0">
                <div className="flex flex-col md:flex-row h-full">
                  <CandidateInfoPanel
                    jobSeeker={data?.organization?.jobPositionInterview?.jobPositionAssignment?.jobSeeker}
                    jobPositionId={data?.organization?.jobPositionInterview?.jobPosition?.id}
                    jobPositionAssignmentId={data?.organization?.jobPositionInterview?.jobPositionAssignment?.id}
                  />
                  <div className="w-full md:w-2/3 p-4 md:p-6">
                    <JobPositionInfoCard
                      jobPosition={data?.organization?.jobPositionInterview?.jobPosition}
                      assignment={firstAssignment}
                    />
                    <InterviewTimeline
                      interviews={data?.organization?.jobPositionInterview?.jobPositionAssignment?.interviews}
                      assignmentId={data?.organization?.jobPositionInterview?.jobPositionAssignment?.id}
                      refetch={refetch}
                    />
                  </div>
                </div>
              </ModalBody>
            </>
          );
        }}
      </ModalContent>
    </Modal>
  );
};

export default InterviewDetailModal;
